import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Sparkles, 
  Bot, 
  Play, 
  Clock, 
  Youtube, 
  CheckCircle2, 
  AlertCircle, 
  TrendingUp, 
  Tv, 
  RefreshCw,
  Power
} from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AutoPilotCard = ({ user, onCycleCompleted, onOpenPayPal }) => {
  const [config, setConfig] = useState({
    enabled: false,
    interval_hours: 4,
    category: "todos",
    last_run: null,
    total_autopilot_published: 0
  });
  const [youtubeChannel, setYoutubeChannel] = useState("Cargando canal...");
  const [loading, setLoading] = useState(true);
  const [runningNow, setRunningNow] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState(null);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [autoRes, ytRes] = await Promise.all([
        axios.get(`${API}/autopilot/status`, { headers, withCredentials: true }),
        axios.get(`${API}/youtube/status`, { headers, withCredentials: true })
      ]);

      setConfig(autoRes.data);
      setYoutubeChannel(ytRes.data?.token_health?.selected_channel_title || "Canal por defecto");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const locked = user?.access_status && !user.access_status.autopilot; // plan Gratis

  const handleToggleAutoPilot = async () => {
    if (locked) {
      onOpenPayPal();
      return;
    }

    const nextState = !config.enabled;
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/autopilot/config`,
        { enabled: nextState, interval_hours: config.interval_hours, category: config.category },
        { headers, withCredentials: true }
      );
      setConfig(prev => ({ ...prev, enabled: nextState }));
      setStatusFeedback({
        type: "success",
        text: nextState 
          ? "¡Modo Piloto Automático activado! La IA generará y publicará shorts virales automáticamente." 
          : "Modo Piloto Automático pausado."
      });
    } catch (e) {
      setStatusFeedback({ type: "error", text: e.response?.data?.detail || "Error actualizando el modo automático." });
    }
  };

  const handleTriggerCycleNow = async () => {
    if (locked) {
      onOpenPayPal();
      return;
    }

    setRunningNow(true);
    setStatusFeedback(null);
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/autopilot/trigger-now`, {}, { headers, withCredentials: true });
      setStatusFeedback({
        type: "success",
        text: `¡Ciclo completado con éxito! Tema elegido por la IA: "${res.data.selected_topic}" y publicado en ${res.data.target_channel}.`
      });
      fetchStatus();
      if (onCycleCompleted) onCycleCompleted(res.data.short);
    } catch (err) {
      if (err.response?.status === 402) {
        onOpenPayPal();
      } else {
        const msg = err.response?.data?.detail || "Error en el ciclo del piloto automático.";
        setStatusFeedback({ type: "error", text: typeof msg === "string" ? msg : JSON.stringify(msg) });
      }
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <div 
      data-testid="autopilot-control-card"
      className="bg-gradient-to-br from-[#12141D] to-[#171B2B] border border-red-500/30 rounded-2xl p-6 shadow-2xl relative overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-white/10 relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-red-600/20 border border-red-500/40 text-red-500 flex items-center justify-center">
            <Bot className="w-6 h-6 animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-black text-white">
                Piloto Automático de YouTube Shorts
              </h3>
              <span className={`text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full ${
                config.enabled 
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" 
                  : "bg-slate-800 text-slate-400"
              }`}>
                {config.enabled ? "ACTIVO & TRABAJANDO" : "EN PAUSA"}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              La IA elige temas virales de alto impacto, redacta el guión, renderiza a 1080×1920 y lo sube a tu canal sin tocar nada.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="toggle-autopilot-btn"
            onClick={handleToggleAutoPilot}
            className={`font-bold text-xs flex items-center gap-1.5 ${
              config.enabled 
                ? "bg-emerald-600 hover:bg-emerald-500 text-white" 
                : "bg-slate-800 hover:bg-slate-700 text-slate-200 border border-white/10"
            }`}
          >
            <Power className="w-3.5 h-3.5" />
            {locked ? "🔒 Disponible con la licencia" : config.enabled ? "Piloto Activado" : "Activar Piloto Automático"}
          </Button>

          <Button
            data-testid="trigger-autopilot-now-btn"
            onClick={handleTriggerCycleNow}
            disabled={runningNow}
            className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white font-black text-xs flex items-center gap-1.5 shadow-lg shadow-red-900/30"
          >
            {runningNow ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5 fill-white" />
            )}
            {runningNow ? "IA Generando y Publicando..." : "Ejecutar Ciclo con IA Ahora"}
          </Button>
        </div>
      </div>

      {statusFeedback && (
        <div 
          data-testid="autopilot-feedback-msg"
          className={`mt-4 p-3 rounded-xl text-xs flex items-start gap-2 relative z-10 ${
            statusFeedback.type === "success" 
              ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" 
              : "bg-red-500/10 border border-red-500/30 text-red-300"
          }`}
        >
          {statusFeedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
          <span>{statusFeedback.text}</span>
        </div>
      )}

      {/* Grid de Configuración Activa del Piloto */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5 relative z-10">
        <div className="p-3.5 rounded-xl bg-black/40 border border-white/5 flex items-center gap-3">
          <Tv className="w-5 h-5 text-red-500 shrink-0" />
          <div className="min-w-0">
            <span className="text-[10px] uppercase font-bold text-slate-400 block">Canal Destino</span>
            <span className="text-xs font-semibold text-white truncate block">
              {youtubeChannel}
            </span>
          </div>
        </div>

        <div className="p-3.5 rounded-xl bg-black/40 border border-white/5 flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="min-w-0">
            <span className="text-[10px] uppercase font-bold text-slate-400 block">Selección de Temas</span>
            <span className="text-xs font-semibold text-white truncate block">
              {config.source === "radar" ? "Radar Viral (tendencias reales)" : "100% Autónomo con IA (Alta Retención)"}
            </span>
          </div>
        </div>

        <div className="p-3.5 rounded-xl bg-black/40 border border-white/5 flex items-center gap-3">
          <Clock className="w-5 h-5 text-cyan-400 shrink-0" />
          <div className="min-w-0">
            <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Publicados por Piloto</span>
            <span className="text-xs font-mono font-bold text-white block">
              {config.total_autopilot_published || 0} Shorts en automático
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
